import { isHiddenGame, isPresenceGameId, SNAKE_PRESENCE_GAME } from "@bing/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useBootSequence } from "./boot/use-boot-sequence";
import { Boot } from "./components/Boot";
import { BottomNav } from "./components/BottomNav";
import { Button } from "./components/Button";
import {
  MaintenanceWarningModal,
  MaintenanceWarningNotice,
} from "./components/MaintenanceWarningModal";
import { PromoSlot } from "./components/PromoSlot";
import { ViewFade } from "./components/ViewFade";
import { WelcomeOverlay } from "./components/WelcomeOverlay";
import { BackgammonGame } from "./game/BackgammonGame";
import { BackgammonOnlineGame } from "./game/BackgammonOnlineGame";
import { MenschGame } from "./game/MenschGame";
import { MenschOnlineGame } from "./game/MenschOnlineGame";
import { BluffGame } from "./game/BluffGame";
import { BluffOnlineGame } from "./game/BluffOnlineGame";
import { CardDeckContext } from "./game/card-decks";
import { loadCardFaces } from "./game/card-faces";
import { prefetchDeck } from "./game/deck-assets";
import { ChessGame } from "./game/ChessGame";
import { DoozGame } from "./game/DoozGame";
import { DoozOnlineGame } from "./game/DoozOnlineGame";
import { SnakeGame } from "./game/snake/SnakeGame";
import { ChessOnlineGame } from "./game/ChessOnlineGame";
import { getCardDeck } from "./settings/prefs";
import { HeartsGame } from "./game/HeartsGame";
import { HeartsOnlineGame } from "./game/HeartsOnlineGame";
import { HokmGame } from "./game/HokmGame";
import { HokmOnlineGame } from "./game/HokmOnlineGame";
import { PasurGame } from "./game/PasurGame";
import { PasurOnlineGame } from "./game/PasurOnlineGame";
import { ShelemGame } from "./game/ShelemGame";
import { ShelemOnlineGame } from "./game/ShelemOnlineGame";
import { fa } from "./i18n/fa";
import { Chat } from "./screens/Chat";
import { Leaderboard } from "./screens/Leaderboard";
import { Friends } from "./screens/Friends";
import { GameSettings } from "./screens/GameSettings";
import { Guides } from "./screens/Guides";
import { History } from "./screens/History";
import { Messages } from "./screens/Messages";
import { Lobby } from "./screens/Lobby";
import { Profile } from "./screens/Profile";
import { ProfileView } from "./screens/ProfileView";
import { BlockedUsers } from "./screens/BlockedUsers";
import { Settings } from "./screens/Settings";
import { Store } from "./screens/Store";
import { ChannelGate, recheckNotice, shouldShowChannelGate } from "./screens/ChannelGate";
import { Maintenance, shouldShowMaintenance } from "./screens/Maintenance";
import { BADGE_POLL_FALLBACK_MS, BADGE_POLL_SAFETY_NET_MS } from "./store/badge-poll";
import { lifecycle } from "./game/lifecycle";
import { TopNoticeStack } from "./game/online-lifecycle";
import { loadOnlineGame } from "./game/online-resume";
import { useSession, useSessionStore } from "./store/context";
import { useViewportReflow } from "./telegram/viewport";

export function App(): ReactNode {
  const status = useSession((s) => s.status);
  const view = useSession((s) => s.view);
  const maintenance = useSession((s) => s.maintenance);
  const channelGate = useSession((s) => s.channelGate);
  const activeGame = useSession((s) => s.activeGame);
  const online = useSession((s) => s.online);
  const error = useSession((s) => s.error);
  const errorAction = useSession((s) => s.errorAction);
  const loadProfile = useSession((s) => s.loadProfile);
  const setView = useSession((s) => s.setView);
  const incomingCount = useSession((s) => s.incomingCount);
  const dmUnreadTotal = useSession((s) => s.dmUnreadTotal);
  const refreshFriends = useSession((s) => s.refreshFriends);
  const refreshDmUnread = useSession((s) => s.refreshDmUnread);
  const refreshInboxUnread = useSession((s) => s.refreshInboxUnread);
  const presenceLive = useSession((s) => s.presenceLive);
  const connectPresence = useSession((s) => s.connectPresence);
  const maintenanceWarning = useSession((s) => s.maintenanceWarning);
  const dismissMaintenanceWarning = useSession((s) => s.dismissMaintenanceWarning);
  const onlineJoinKind = useSession((s) => s.onlineJoin.kind);
  const rejoinOnline = useSession((s) => s.rejoinOnline);
  const sessionStore = useSessionStore();

  // THE GATE OWNS THE SCREEN UNTIL IT SAYS OTHERWISE.
  //
  // A passing membership check clears `channelGate` (the api-client does it on the first 200),
  // which used to yank the gate off the screen the instant the user tapped «عضو شدم» — no
  // confirmation, no transition, just a bare lobby. So once we have BEEN gated, the gate stays
  // mounted (now showing its approval beat) until it hands the screen over itself.
  const gateActive = shouldShowChannelGate(channelGate, view, status);
  const wasGated = useRef(false);
  // ⚠️ IN THE GAME VIEW THE LATCH ARMS ONLY ON A REFUSAL. There the gate also shows while a reload
  // is in flight under a gate a background read armed — and a latch armed THEN made a reload that
  // came back 200 end in «✅ عضویتت تأیید شد» over a READY table for the 5 s hold. The beat answers a
  // refusal the player saw; a table-link boot's refusal is `status === "error"` and arms it as before.
  if (gateActive && (view !== "game" || status === "error")) wasGated.current = true;
  const [gateEntered, setGateEntered] = useState(false);
  useEffect(() => {
    // Gated again (they left a channel) → a future pass gets its own beat.
    if (gateActive) setGateEntered(false);
  }, [gateActive]);
  const disconnectPresence = useSession((s) => s.disconnectPresence);
  const setInGame = useSession((s) => s.setInGame);
  const profile = useSession((s) => s.profile);

  // Branded staged boot: owns loadProfile + the staggered friends/DM fetches and
  // drives the splash's captions/progress. Reveals the app on ready + a ~2.2s floor.
  const boot = useBootSequence();

  // THE TABLE, ONCE ON SCREEN, STAYS ON SCREEN UNTIL THE PLAYER LEAVES THE GAME VIEW.
  //
  // Unmounting a live table is not a repaint: its cleanup is a CONSENTED leave (the seat to a bot at
  // once; both partners of a public حکم table hit ⇒ the match ENDED with rated wins for the other
  // side). And a table can now be entered while the session is still «error» — from the maintenance
  // screen's «بازگشت به بازی» after a reload during maintenance (operator, 2026-10-04: maintenance
  // stops NEW games only). The OFF that follows flips that session to «ready» — and the api client
  // clears the flag a moment BEFORE the store writes «ready», with nothing guaranteeing one commit for
  // both. A game branch whose CONDITION followed `status` would drop the table in either frame.
  //
  // So the game view renders from ONE branch, FIRST in `content` below. Its ENTRY is what it always
  // was (`boot.done` past the gate, which is how a table link's boot still shows the splash, the
  // gate or the error screen before any table mounts) plus exactly one new door: a `rejoin`, whatever
  // the session says. A BUTTON sets one — the lobby strip (on «ready»), the maintenance screen's
  // (under «error», the case this exists for) and the crash screen's (card-a5 item 3: the app remounts
  // in place, and its table must not wait for the boot it re-runs) — and so, ON PURPOSE, does the app
  // itself on open when this device holds a PUBLIC seat (card-a5 item 1, `boot/open-route.ts`): «reopening
  // the app while the seat is still held goes straight back to the table», so that table IS the first
  // screen, ahead of the boot. Nothing else can come through it (a table LINK is a `join`, which waits
  // for the boot as before). Once in, nothing but leaving the view takes the table down. The latch is written during render,
  // like `wasGated` above: idempotent, so a discarded render cannot hurt (and the table it mounts
  // connects only once that render commits — `OnlineGame`'s `deferConnect`).
  const gateOwnsScreen = gateActive || (wasGated.current && !gateEntered);
  const tableShown = useRef(false);
  if (view !== "game") tableShown.current = false;
  else if (!gateOwnsScreen && (boot.done || onlineJoinKind === "rejoin")) tableShown.current = true;
  const showTable = tableShown.current;

  // WARM THE CARD ARTWORK ONCE BOOT IS DONE — not before.
  //
  // ~90% of players open this app for حکم, so the 155 KB (gzipped) face chunk is effectively
  // required for almost everyone; fetching it while they read the lobby means it is already there
  // when they tap a game, and the loading gate never has to wait.
  //
  // ⚠️ THE `boot.done` CONDITION IS THE WHOLE POINT — DO NOT HOIST THIS. Measured 2026-08-03: the
  // 94.7 KB stylesheet failed 4.06% of the time while it downloaded ALONGSIDE the 1.38 MB bundle,
  // against 0.14% for a 69 KB asset fetched after boot. Prefetching during boot would put this
  // chunk in contention with the one fetch that cannot be retried and could make boot WORSE.
  //
  // THEN THE PLAYER'S OWN DECK (2026-10-20): an image deck is downloaded in the background after the
  // classic chunk — never alongside it (the same contention) — so that by the time they open a
  // table it is normally in; a table that opens first deals «استاندارد» until it is
  // (`game/deck-assets.ts`). A no-op for the classic deck, which IS the chunk.
  useEffect(() => {
    if (!boot.done) return;
    const id = window.setTimeout(() => {
      void loadCardFaces()
        .catch(() => undefined)
        .finally(() => prefetchDeck(getCardDeck()));
    }, 0);
    return () => window.clearTimeout(id);
  }, [boot.done]);

  // One-time first-entry welcome — ONLY brand-new accounts (server `isNew`, true on the
  // first-ever GET /me), and once per device (localStorage guard so a same-session
  // re-mount can't reflash it). Existing users never see it.
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (status !== "ready" || !profile?.isNew) return;
    try {
      if (localStorage.getItem("bing:welcomed") === "1") return;
    } catch {
      /* storage-less env → still show once */
    }
    setShowWelcome(true);
  }, [status, profile?.isNew]);
  function dismissWelcome(): void {
    setShowWelcome(false);
    try {
      localStorage.setItem("bing:welcomed", "1");
    } catch {
      /* ignore */
    }
  }

  // Re-layout on Telegram viewport changes so the shell tracks --app-h — fixes the
  // first-paint bug where the bottom nav + cards needed a scroll to appear.
  useViewportReflow();

  // THE BADGES ARE PUSHED NOW; THIS IS WHAT CATCHES WHAT THE PUSH MISSES.
  //
  // Every write that moves the three badge reads publishes a `data-changed` (see
  // `presence.ts` → `onDataChanged`), so the interval below is no longer the delivery
  // mechanism — except while the socket is DOWN, where there are no pushes at all and it
  // still is. Hence two cadences, and only two; the numbers and their derivation live in
  // `store/badge-poll.ts`.
  //
  // ⚠️ IT DEPENDS ON `presenceLive`, WHICH IS A BOOLEAN OFF THE STORE — never on a socket
  // object. An effect keyed on an object identity re-runs on every unrelated update, clears
  // its own timer and can fail to re-arm; that trap is in `## Gotchas` and has bitten three
  // times in `DoozTable.tsx` alone.
  useEffect(() => {
    if (status !== "ready") return;
    // The boot sequence fires the FIRST friends/DM fetch (staggered) but never the inbox —
    // so this is the inbox's own first read, exactly as before.
    void refreshInboxUnread();
  }, [status, refreshInboxUnread]);

  useEffect(() => {
    if (status !== "ready") return;
    const everyMs = presenceLive ? BADGE_POLL_SAFETY_NET_MS : BADGE_POLL_FALLBACK_MS;
    const id = window.setInterval(() => {
      void refreshFriends();
      void refreshDmUnread();
      void refreshInboxUnread();
    }, everyMs);
    return () => window.clearInterval(id);
  }, [status, presenceLive, refreshFriends, refreshDmUnread, refreshInboxUnread]);

  // RESYNC THE MOMENT THE APP COMES BACK. A suspended WebView fires no timers at all — measured
  // on prod, a socket-down client polls at 1.5 % of its cadence — so returning to the foreground
  // is precisely when the badges are most likely to be stale. The push cannot help: it was sent
  // while nothing was listening. The socket's own return is covered by `onReconnected`; this
  // covers the case where the socket survived the background and only the timers stopped.
  useEffect(() => {
    if (status !== "ready") return;
    let wasActive = lifecycle.appActive;
    return lifecycle.subscribe((state) => {
      if (state.appActive === wasActive) return;
      wasActive = state.appActive;
      if (!state.appActive) return;
      void refreshFriends();
      void refreshDmUnread();
      void refreshInboxUnread();
    });
  }, [status, refreshFriends, refreshDmUnread, refreshInboxUnread]);

  // Background presence/friend-realtime socket: connect once the session is ready
  // and tear down on teardown. Live pushes drive friends' presence + a refresh on
  // an incoming request/accept (the poll above is now just a fallback).
  useEffect(() => {
    if (status !== "ready") return;
    connectPresence();
    return () => disconnectPresence();
  }, [status, connectPresence, disconnectPresence]);

  // Reflect in-game presence: the user is "in a game" exactly while the game view
  // is open (friends see «در حال بازی»), tagged with WHICH game so the lobby's
  // live per-game counters stay accurate — «مار» included, under its presence-only
  // key; null again on leaving.
  useEffect(() => {
    setInGame(view === "game", view === "game" && isPresenceGameId(activeGame) ? activeGame : null);
  }, [view, activeGame, setInGame]);

  // Render the active screen once (every early-return branch lives in this IIFE), so the app-wide
  // maintenance-warning overlay (below) can sit on top of WHATEVER it is — the lobby OR a live game.
  const content = ((): ReactNode => {
    // Full-screen game rooms (no tab bar) — ONE branch, FIRST, so no screen below can replace a
    // table that is on screen (see `showTable`, above).
    if (showTable) {
      // Online multiplayer ships for every multi-human game; each has a "<game>-online"
      // room. Anything unmatched falls through to solo.
      if (online) {
        switch (activeGame) {
          case "hearts":
            return <HeartsOnlineGame />;
          case "hokm":
            return <HokmOnlineGame />;
          case "shelem":
            return <ShelemOnlineGame />;
          case "pasur":
            return <PasurOnlineGame />;
          case "bluff":
            return <BluffOnlineGame />;
          case "chess":
            return <ChessOnlineGame />;
          case "backgammon":
            return <BackgammonOnlineGame />;
          case "mensch":
            return <MenschOnlineGame />;
          case "dooz":
            return <DoozOnlineGame />;
        }
      }
      // Chess is NOT a card game — it renders OUTSIDE the CardDeckContext wrapper (no card
      // decks). Its ONLINE container is routed in the switch above; this is the SOLO path
      // (reached only when `online` is false).
      if (activeGame === "chess") {
        return <ChessGame />;
      }
      // Backgammon is also NOT a card game — it renders OUTSIDE the CardDeckContext wrapper (like
      // chess). Its ONLINE container is routed in the switch above; this is the SOLO path (reached
      // only when `online` is false).
      if (activeGame === "backgammon") {
        return <BackgammonGame />;
      }
      // منچ is a dice/board game too — renders OUTSIDE the CardDeckContext (no card decks).
      if (activeGame === "mensch") {
        return <MenschGame />;
      }
      // دوز is a board game as well — no card decks, so it renders outside the provider too.
      if (activeGame === "dooz") {
        return <DoozGame />;
      }
      // «مار» is played wholly on the phone: no room, no card decks — and never online.
      if (activeGame === SNAKE_PRESENCE_GAME) {
        return <SnakeGame />;
      }
      // SOLO games render with the player's per-user deck (offline pref); the shared
      // Card reads it via CardDeckContext, so no table threads `deck` through its cards.
      const soloGame =
        activeGame === "pasur" ? (
          <PasurGame />
        ) : activeGame === "hokm" ? (
          <HokmGame />
        ) : activeGame === "bluff" ? (
          <BluffGame />
        ) : activeGame === "shelem" ? (
          <ShelemGame />
        ) : (
          <HeartsGame />
        );
      return <CardDeckContext.Provider value={getCardDeck()}>{soloGame}</CardDeckContext.Provider>;
    }

    // MAINTENANCE — the platform is being updated and this user is not an admin (the server
    // refuses every request with 503 `MAINTENANCE`; admins are exempt, checked server-side).
    //
    // Deliberately NOT shown over a live game: `view === "game"` means a game is in progress,
    // and an in-progress game is allowed to FINISH. The game runs on the game-server's socket,
    // which the api's maintenance gate never touches, and the game-server blocks only NEW joins
    // — so the player plays on, and sees this the moment they return to the lobby. It clears
    // itself on the next successful response, so the app comes back when maintenance ends.
    // ⚠️ And it offers the way BACK to a live table — this device's ticket; see `Maintenance`.
    if (shouldShowMaintenance(maintenance, view)) {
      const saved = loadOnlineGame();
      // A game hidden from players is never offered back (the lobby strip's own rule).
      const ticket = saved && !isHiddenGame(saved.game) ? saved : null;
      return (
        <Maintenance
          message={maintenance?.message}
          ticket={ticket}
          onReturn={
            ticket ? () => rejoinOnline(ticket.game, ticket.roomId, ticket.token) : undefined
          }
        />
      );
    }

    // MANDATORY-CHANNEL GATE — a non-member of the required channels cannot open/use the mini app
    // (the api refuses every request with 403 `CHANNELS_REQUIRED`, from the SAME `RequiredChannel`
    // config as the bot's `/start` gate). Like maintenance, NOT shown over a live game — the game
    // view on a READY session; a table link's boot is in the game view long before that (see
    // `shouldShowChannelGate`) — and it clears itself on the next successful response after «عضو
    // شدم». Server-side is authoritative; this is only the join UI.
    if (gateOwnsScreen) {
      return (
        <ChannelGate
          channels={channelGate ?? []}
          // The server cleared the gate ⇒ they are IN. Say so, then dissolve into the app.
          approved={!gateActive}
          // A re-check with NO verdict (Telegram did not answer in time: the api's 503) gets its own
          // line on the gate, not «هنوز عضو…» — card-a5 item 5.
          onRecheck={async () => {
            await loadProfile();
            return recheckNotice(sessionStore.getState());
          }}
          onEnter={() => {
            wasGated.current = false;
            setGateEntered(true);
          }}
        />
      );
    }

    // Branded staged splash until the profile is ready AND the ~2.2s floor elapsed
    // (useBootSequence owns loadProfile + the staggered data fetches). `error` falls
    // through to the retry screen below.
    if (!boot.done && status !== "error") {
      // The true app-boot splash hosts the SPLASH promo slot (renders nothing today).
      return (
        <Boot
          caption={boot.caption}
          progress={boot.progress}
          splash={<PromoSlot slot="SPLASH" />}
        />
      );
    }

    if (status === "error") {
      return (
        <div
          style={{
            height: "var(--app-h)",
            display: "grid",
            placeItems: "center",
            background: "var(--room-bg)",
            padding: 24,
            textAlign: "center",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
            <p
              style={{
                color: "var(--danger)",
                margin: 0,
                fontWeight: 700,
                // A multi-line message (e.g. the offline/VPN advice) keeps its line breaks.
                whiteSpace: "pre-line",
                lineHeight: 1.7,
              }}
            >
              {error ?? fa.status.error}
            </p>
            {/* THE ACTION, AND ONLY ONE THAT CAN WORK — see `SessionState.errorAction`. A boot
                that failed on a 4xx verdict (opened outside Telegram, a banned account) gets the
                same answer however often it is asked, so it gets no button at all: the message
                above already says the one thing that helps. A STALE SESSION gets a reload rather
                than a retry, because re-asking sends the same expired initData and only a fresh
                page can mint new credentials. */}
            {errorAction === "retry" ? (
              <Button variant="primary" onClick={() => void loadProfile()}>
                {fa.status.retry}
              </Button>
            ) : null}
            {errorAction === "reload" ? (
              <Button variant="primary" onClick={() => window.location.reload()}>
                {fa.status.reopen}
              </Button>
            ) : null}
          </div>
        </div>
      );
    }

    // Host's pre-create private-table settings — full-screen, no tab bar.
    if (view === "gameSettings") {
      return <GameSettings />;
    }

    // (The game view is not down here any more: it is the FIRST branch above, `showTable`.)

    const screen =
      view === "profile" ? (
        <Profile />
      ) : view === "userProfile" ? (
        <ProfileView />
      ) : view === "friends" ? (
        <Friends />
      ) : view === "chat" ? (
        <Chat />
      ) : view === "leaderboard" ? (
        <Leaderboard />
      ) : view === "store" ? (
        <Store />
      ) : view === "settings" ? (
        <Settings />
      ) : view === "blockedUsers" ? (
        <BlockedUsers />
      ) : view === "help" ? (
        <Guides />
      ) : view === "history" ? (
        <History />
      ) : view === "messages" ? (
        <Messages />
      ) : (
        <Lobby />
      );

    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "var(--bg)",
          color: "var(--text)",
          // TRUE-FULLSCREEN safe area (the piece that broke last time): the app
          // fills the screen edge-to-edge, but the TAB SCREENS start below the
          // status bar / Dynamic Island / Telegram chrome so the header (profile +
          // hamburger) stays visible and tappable. env() covers windowed iOS;
          // --tg-safe-top (SDK safeAreaInsets + contentSafeAreaInsets, bound live
          // in telegram/viewport.ts) covers fullscreen where iOS zeroes env().
          // Live CSS var → correct on first paint, self-updating on
          // safe_area_changed. Game tables render OUTSIDE this shell and pad
          // their own HUDs (max(env, --tg-safe) sweep); BottomNav handles the
          // bottom inset itself.
          paddingTop: "max(env(safe-area-inset-top), var(--tg-safe-top, 0px))",
        }}
      >
        {/* Keyed by `view`: every tab/screen switch replays the quick view-enter
          transition (and starts scrolled to the top). */}
        <ViewFade
          viewKey={view}
          style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
        >
          {screen}
        </ViewFade>
        <BottomNav
          active={view}
          onNavigate={setView}
          friendsBadge={incomingCount}
          chatBadge={dmUnreadTotal}
        />
        {showWelcome ? <WelcomeOverlay onClose={dismissWelcome} /> : null}
      </div>
    );
  })();

  // The pre-deploy maintenance warning shows over WHATEVER `content` is — the lobby OR a live online
  // game — so a mid-game player is warned before the operator deploys. It does NOT disconnect anyone;
  // «متوجه شدم» dismisses it client-side.
  // ⚠️ AT A TABLE IT IS A NOTICE, NEVER THE MODAL (review, 2026-10-04: 6 of 6). The modal's scrim
  // swallowed the taps on the hand and on «خروج» while the turn timers ran on, so the server played
  // the turns and then removed the player for idling — the warning cost the very table it was
  // protecting. In the game view it sits in the one top-notice positioner, which lets every tap
  // through but its own button's — one small line that goes by itself after 12 s, because that slot
  // is the top of the felt (the حکم partner's seat). (Outside the game view nothing is on a clock:
  // the modal stays, and waits for «متوجه شدم».)
  return (
    <>
      {content}
      {maintenanceWarning ? (
        view === "game" ? (
          <TopNoticeStack>
            <MaintenanceWarningNotice
              warning={maintenanceWarning}
              onDismiss={dismissMaintenanceWarning}
            />
          </TopNoticeStack>
        ) : (
          <MaintenanceWarningModal
            etaMinutes={maintenanceWarning.etaMinutes}
            onDismiss={dismissMaintenanceWarning}
          />
        )
      ) : null}
    </>
  );
}
